import { PreservingNavigate } from "@components/common/preserve-navigation/preserving-navigate";
import { MainPageLayout } from "@components/page-layout/main-page-layout";
import { RouteScopedProviders } from "@context-providers/route-scoped-providers";
import { ProjectArchivingState } from "@custom-types/project-types";
import { LoadingScreen } from "@flat-ui";
import { AccountSettingsPage } from "@pages/account-settings/account-settings-page";
import { AnalyticsPage } from "@pages/analytics/analytics-page";
import { DataManagement } from "@pages/data-management/data-management";
import { ForbiddenPage } from "@pages/forbidden-page";
import { GroupDetails } from "@pages/group-details/group-details";
import { GroupsPage } from "@pages/groups/groups-page";
import { MembersWorkspace } from "@pages/members/members-workspace";
import { TeamPage } from "@pages/members/teams/team-page";
import { MemberPage } from "@pages/profile/member-page";
import { ProjectDetails } from "@pages/project-details/project-details";
import { ValidateProjectStatus } from "@pages/projects/validate-project-status";
import { RedirectUnknownRoutes } from "@pages/redirect-unknown-routes";
import { AddCreditsPage } from "@pages/settings/subscription-settings/credits/add-credits-page";
import { CreditsPurchaseRedirection } from "@pages/settings/subscription-settings/credits/credits-purchase-redirection";
import { WorkspaceSettingsPage } from "@pages/settings/workspace-settings-page";
import { SubscriptionExpiredPage } from "@pages/subscription-expired/subscription-expired-page";
import { AuthenticatedRoute } from "@router/authenticated-route";
import { CustomErrorBoundary } from "@router/custom-error-boundary";
import {
  AnalyticsTabs,
  CreditRoutes,
  GroupTabs,
  HBEntryPageRoutes,
  MainRoutes,
  MemberTabs,
  ProjectTabs,
  SettingsTabs,
  TeamTabs,
  UsersTabs,
} from "@router/route-params";
import * as Sentry from "@sentry/react";
import { App } from "@src/App";
import { runtimeConfig } from "@src/runtime-config";
import { RequiredRoleCompanyLevelName } from "@utils/access-control/company/company-access-control-types";
import { RequiredFeatureCompanyLevelName } from "@utils/feature-control/company/company-feature-control-types";
import { constructHBEntryPageParam } from "@utils/route-utils";
import {
  getAnalyticsBreadcrumbHandle,
  getGroupBreadcrumbHandle,
  getGroupsBreadcrumbHandle,
  getMemberProfileBreadcrumbHandle,
  getMembersBreadcrumbHandle,
  getProjectBreadcrumbHandle,
  getProjectsBreadcrumbHandle,
  getTeamBreadcrumbHandle,
  getWorkspaceSettingsBreadcrumbHandle,
} from "@utils/sphere-breadcrumb-utils";
import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";

/**
 * Sentry's React Router integration
 * https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/#react-router-v6
 */
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route element={<RouteScopedProviders />}>
      {/* Root page that will prompt the user to select a company first. */}
      <Route
        path={MainRoutes.root}
        element={<App />}
        errorElement={<CustomErrorBoundary />}
      />

      {/* HBD route "/me" should also be accessible as guest user which will redirect to account setting page  */}
      <Route path={`${MainRoutes.app}/me`} Component={RedirectUnknownRoutes} />

      <Route path={MainRoutes.accountSettings}>
        {/* Account settings page /account */}
        <Route index element={<AccountSettingsPage />} />
        {/* Account settings page /account/:accountSettingsTab */}
        <Route path={":accountSettingsTab"} element={<AccountSettingsPage />} />
      </Route>

      {/* Company SSO route that will redirect to `${runtimeConfig.urls.sphereEntryPageUrl}/sso-login?company=${ssoId}` */}
      <Route
        path={HBEntryPageRoutes.ssoRoute}
        loader={({ params }) => {
          // Pass the ssoId from the route if available to the sphere entry page
          let targetHref = `${runtimeConfig.urls.sphereEntryPageUrl}/sso-login`;

          const ssoId = params?.ssoId;
          if (ssoId) {
            targetHref += `?company=${ssoId}`;
          }

          window.location.href = targetHref;
          return <LoadingScreen />;
        }}
      />

      {/* All routes of the deprecated HB EntryPage should direct to the root page */}
      {Object.values(HBEntryPageRoutes)
        .filter((route) => route !== HBEntryPageRoutes.ssoRoute)
        .map((route) => (
          <Route
            key={route}
            path={route}
            element={
              <Navigate
                to={{
                  pathname: MainRoutes.root,
                  search: constructHBEntryPageParam(route),
                }}
                replace
              />
            }
          />
        ))}
      {/* Logout route that will redirect to `${runtimeConfig.urls.sphereEntryPageUrl}/logout` */}
      <Route
        path={MainRoutes.logout}
        loader={() => {
          window.location.href = `${runtimeConfig.urls.sphereEntryPageUrl}/logout`;
          return null;
        }}
      />
      {/* Main page with company id to instantiate the App component /:companyId */}
      <Route
        path={MainRoutes.app}
        element={<App />}
        errorElement={<CustomErrorBoundary />}
      >
        <Route path={MainRoutes.credits} element={<AddCreditsPage />} />
        <Route
          path={`${MainRoutes.credits}/${CreditRoutes.purchaseSuccess}`}
          element={<CreditsPurchaseRedirection />}
        />

        {/* Defines the layout of the page that it is visible in all pages,
            including the header, breadcrumbs and page content */}
        <Route element={<MainPageLayout />}>
          {/* Route page with company id to redirect to projects page /:companyId/ */}
          <Route
            path={`${MainRoutes.app}/`}
            element={
              <PreservingNavigate
                to={`${MainRoutes.projects}/${ProjectArchivingState.active}`}
              />
            }
          />
          {/* Projects page before project status to /:companyId/projects */}
          <Route
            path={MainRoutes.projects}
            handle={getProjectsBreadcrumbHandle()}
          >
            {/* Projects page to redirect to /:companyId/projects/active */}
            <Route
              index
              element={
                <PreservingNavigate to={ProjectArchivingState.active} replace />
              }
            />
            {/* Projects page with project status /:companyId/projects/:projectStatus */}
            <Route path={":projectStatus"}>
              <Route index element={<ValidateProjectStatus />} />
            </Route>
          </Route>

          <Route
            path={MainRoutes.projects}
            handle={getProjectsBreadcrumbHandle()}
          >
            {/* Project page /:companyId/project/:projectId */}
            <Route path={":projectId"} handle={getProjectBreadcrumbHandle()}>
              {/* Project details that redirects to overview tab */}
              {/* /:companyId/projects:projectStatus/:projectId */}
              <Route
                index
                element={<PreservingNavigate to={ProjectTabs.overview} />}
              />

              {/* <Redirect from="teams" to="members" /> */}
              <Route path={"team"} element={<RedirectUnknownRoutes />} />

              {/* Project details page using page tabs /:companyId/projects/:projectId/:projectTabs */}
              <Route path={":projectTabs"} element={<ProjectDetails />} />
            </Route>
          </Route>

          {/* Members route: /members */}
          <Route
            path={MainRoutes.members}
            handle={getMembersBreadcrumbHandle()}
          >
            <Route
              index
              element={<PreservingNavigate to={UsersTabs.members} replace />}
            />

            {/* Settings page with settings type: /:companyId/users/:usersTab */}
            <Route path={":usersTab"}>
              <Route
                index
                element={
                  <AuthenticatedRoute
                    key={"members"}
                    requiredFeatureCompanyLevel={
                      RequiredFeatureCompanyLevelName.canViewCompanyMembers
                    }
                  >
                    <MembersWorkspace />
                  </AuthenticatedRoute>
                }
              />
            </Route>

            {/* Member profile page: users/members/:memberId */}
            <Route
              path={`${UsersTabs.members}/:memberId`}
              handle={getMemberProfileBreadcrumbHandle()}
            >
              <Route
                index
                element={<PreservingNavigate to={MemberTabs.overview} />}
              />

              {/* Member page tab: users/members/:memberId/:memberTabs */}
              <Route
                path={":memberTabs"}
                element={
                  <AuthenticatedRoute
                    key={"member-page"}
                    requiredRoleCompanyLevel={
                      RequiredRoleCompanyLevelName.canViewCompanyMemberProfile
                    }
                  >
                    <MemberPage />
                  </AuthenticatedRoute>
                }
              />
            </Route>

            {/* Team details page: users/teams/:teamId */}
            <Route
              path={`${UsersTabs.teams}/:teamId`}
              handle={getTeamBreadcrumbHandle()}
            >
              <Route
                index
                element={<PreservingNavigate to={TeamTabs.members} />}
              />

              {/* Team details page tab: users/teams/:teamId/:teamTabs */}
              <Route
                path={":teamTabs"}
                element={
                  <AuthenticatedRoute
                    key={"team-details"}
                    requiredRoleCompanyLevel={
                      RequiredRoleCompanyLevelName.canViewTeams
                    }
                  >
                    <TeamPage />
                  </AuthenticatedRoute>
                }
              />
            </Route>
          </Route>

          {/* Groups route: /groups */}
          <Route path={MainRoutes.groups} handle={getGroupsBreadcrumbHandle()}>
            {/* Groups page: / */}
            <Route
              index
              element={
                <AuthenticatedRoute
                  key={"groups"}
                  requiredFeatureCompanyLevel={
                    RequiredFeatureCompanyLevelName.canViewCompanyGroups
                  }
                >
                  <GroupsPage />
                </AuthenticatedRoute>
              }
            />
            {/* Group details page: /:groupId */}
            <Route path={":groupId"} handle={getGroupBreadcrumbHandle()}>
              <Route
                index
                element={<PreservingNavigate to={GroupTabs.managers} />}
              />
              {/* Group details page tab: /:groupTabs */}
              <Route
                path={":groupTabs"}
                element={
                  <AuthenticatedRoute
                    key={"group-details"}
                    requiredFeatureCompanyLevel={
                      RequiredFeatureCompanyLevelName.canViewCompanyGroups
                    }
                  >
                    <GroupDetails />
                  </AuthenticatedRoute>
                }
              />
            </Route>
          </Route>

          {/* Analytics page before analytics type to /:companyId/analytics */}
          <Route
            path={MainRoutes.analytics}
            handle={getAnalyticsBreadcrumbHandle()}
          >
            {/* Analytics page to redirect to /:companyId/analytics/overview */}
            <Route
              index
              element={
                <PreservingNavigate to={AnalyticsTabs.workspace} replace />
              }
            />

            {/* Analytics page with analytics type /:companyId/analytics/:analyticsTabs */}
            <Route path={":analyticsTabs"}>
              <Route
                index
                element={
                  <AuthenticatedRoute
                    key={"analytics"}
                    requiredFeatureCompanyLevel={
                      RequiredFeatureCompanyLevelName.canViewAnalytics
                    }
                  >
                    <AnalyticsPage />
                  </AuthenticatedRoute>
                }
              />
            </Route>
          </Route>

          {/* Workspace settings page /:companyId/settings */}
          <Route
            path={MainRoutes.settings}
            handle={getWorkspaceSettingsBreadcrumbHandle()}
          >
            <Route
              index
              element={<PreservingNavigate to={SettingsTabs.general} replace />}
            />

            {/* Settings page with settings type: /:companyId/settings/:settingsTab */}
            <Route path={":settingsTab"}>
              <Route
                index
                element={
                  <AuthenticatedRoute
                    key={"workspace-settings"}
                    requiredFeatureCompanyLevel={
                      RequiredFeatureCompanyLevelName.canViewWorkspaceSettings
                    }
                  >
                    <WorkspaceSettingsPage />
                  </AuthenticatedRoute>
                }
              />
            </Route>

            {/* <Redirect from="settings/integrations" to="integrations" /> */}
            <Route path={"integrations"} element={<RedirectUnknownRoutes />} />
          </Route>

          {/* Forbidden page when the user does not have enough permissions */}
          <Route path={MainRoutes.forbidden} Component={ForbiddenPage} />
        </Route>

        {/* Redirects to the Viewer's Data Management editor */}
        <Route
          path={`${MainRoutes.app}/${MainRoutes.projects}/:projectId/${MainRoutes.dataManagement}`}
          element={<DataManagement />}
        />

        {/* Subscription expired page /subscription-expired */}
        <Route
          path={`${MainRoutes.app}/${MainRoutes.subscriptionExpired}`}
          Component={SubscriptionExpiredPage}
        />

        {/* Default shows a not found page */}
        <Route path="*" Component={RedirectUnknownRoutes} />
      </Route>
    </Route>
  ),
  {
    // Enable future flags that introduces API breaking changes.
    // This allows to opt in to one change a time: https://reactrouter.com/en/main/guides/api-development-strategy
    future: {
      /* eslint-disable @typescript-eslint/naming-convention -- keys coming from external package */
      v7_partialHydration: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_skipActionErrorRevalidation: true,
      /* eslint-enable */
    },
  }
);
